import { sortBy } from "lodash-es";
import { type CSSProperties, type FC, useCallback, useEffect, useMemo, useState } from "react";
import "./GroupCardShared.scss";
import "./GroupConfigCard.scss";

import { toastLocalizedOperationCatcher } from "../../../../utils/alerts";
import { reorderItemInCollection } from "../../../../utils/collections";

import { serverDistributeUsersToGroups } from "api/api-server-actions";
import { useIsAssessing } from "components/materials/course/assessment/hooks";
import {
  getActiveStateSortKeyForUser,
  userActivityIcons,
} from "components/materials/course/helpers";
import { GroupConfigBreakoutsBanner } from "components/materials/page/groups/GroupConfigBreakoutsBanner";
import { PageGroupCard } from "components/materials/page/groups/PageGroupCard";
import { StudentOptionsMenu } from "components/materials/page/groups/StudentOptionsMenu";
import {
  DraggableCourseUserType,
  DraggablePageGroupUserType,
  getUngroupedStudentUsers,
  hasTotalUsersReachedMaxGroupSize,
} from "components/materials/page/groups/helpers";
import { GroupSizeConfig } from "components/materials/page/groups/menus/GroupSizeConfig";
import { InteractionModeConfig } from "components/materials/page/groups/menus/InteractionModeConfig";
import { InteractionMode, PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Card } from "mds/components/Card";
import { CloseButton } from "mds/components/CloseButton";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { LegacyButton } from "mds/components/LegacyButton";
import { ListItem } from "mds/components/ListItem";
import { TextAddButton } from "mds/components/TextAddButton";
import { Tooltip } from "mds/components/Tooltip";
import { LoadingDotsIcon, PersonSingleIcon } from "mds/icons";
import { useUserIdsWhoCanParticipateInCourse } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  CourseUserActivityStatus,
  NestedPageGroupType,
  UserWithCourseActivity,
  selectHasAssessmentsForPageId,
  selectHasSubmissionsForPageId,
  selectUsersWithCourseActivityInCurrentCourse,
} from "store/selectors";
import { getUserLastNameSortKey } from "store/selectors/sorting";
import { viewStateActions } from "store/slices/view";
import { useListSelector } from "store/store-hooks";
import { useAv } from "utils/av";
import { getUserFullName } from "utils/user-utils";

interface GroupConfigCardProps {
  page: PageType;
  pageGroups: NestedPageGroupType[];
}

export const GroupConfigCard: FC<GroupConfigCardProps> = ({ page, pageGroups }) => {
  const { isInVirtualClass } = useAv();

  const dispatch = useAppDispatch();

  const hideGroupConfig = useCallback(
    () => dispatch(viewStateActions.setShowGroupConfig(false)),
    [dispatch],
  );

  const submissionsExist = useAppSelector((s) => selectHasSubmissionsForPageId(s, page.id));
  const hasAssessments = useAppSelector((s) => selectHasAssessmentsForPageId(s, page.id));
  const isAssessing = useIsAssessing();
  const studentUserIds = useUserIdsWhoCanParticipateInCourse();
  const users = useListSelector(selectUsersWithCourseActivityInCurrentCourse);
  const studentUsers = useMemo(
    () =>
      sortBy<UserWithCourseActivity>(users, [
        getActiveStateSortKeyForUser,
        getUserLastNameSortKey,
      ]).filter((user) => studentUserIds.includes(user.id)),
    [users, studentUserIds],
  );

  const [isDistributingStudents, setIsDistributingStudents] = useState(false);
  const hasSubmissions = Boolean(submissionsExist || page.submissions_last_collected_at);
  const isReadOnly = hasAssessments || isAssessing;

  const [localUngrouped, setLocalUngrouped] = useState<DraggableCourseUserType[] | null>(null);
  const derivedUngrouped = getUngroupedStudentUsers(pageGroups, studentUsers);
  const ungroupedStudentUsers = localUngrouped ?? derivedUngrouped;

  // Reset local override when group membership (for these pageGroups) changes
  const membershipHash = useMemo(() => {
    return pageGroups
      .flatMap((pg) => pg.page_group_users.map((pgu) => `${pg.id}:${pgu.course_user_id}`))
      .sort()
      .join("|");
  }, [pageGroups]);
  useEffect(() => {
    if (localUngrouped !== null) {
      setLocalUngrouped(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipHash]);

  // moveCourseUserLocally handles drag events in both GroupConfigCard and PageGroupCard. All it
  // does is update the page_group_id of the dragged item and set it equal to the dropContainerId
  // of whatever drop zone it's hovering over. This way when onDropCourseUser is called, the item
  // will have the new value for page_group_id already set. The value of dropContainerId will be
  // a PageGroup id when hovering over a PageGroupCard, or the special value "ungrouped" when
  // hovering over the ungrouped students list.
  const moveCourseUserLocally = (
    item: DraggableCourseUserType | DraggablePageGroupUserType,
    _toIndex: number,
    dropContainerId: string,
  ) => {
    // eslint-disable-next-line no-param-reassign
    item.page_group_id = dropContainerId;
  };

  // moveRemovedStudentToTop is passed down through PageGroupCard into StudentOptionsMenu where it is
  // used to handle clicks on the "Remove from this Group" button. StudentOptionsMenu performs the
  // DELETE request on the PageGroupUser which causes it to disappear from the PageGroupCard and appear
  // in the ungrouped students list, and then moveRemovedStudentToTop moves it to the top of that list.
  const moveRemovedStudentToTop = (studentCourseUserId: string) => {
    const removed = studentUsers.find((u) => u.course_user_id === studentCourseUserId);
    if (!removed) return;
    const item: DraggableCourseUserType = { ...removed, order: -1 } as DraggableCourseUserType;
    const collection: DraggableCourseUserType[] = ungroupedStudentUsers.map((u) => ({ ...u }));
    const newCollection = reorderItemInCollection(item, collection, 0);
    setLocalUngrouped(newCollection);
  };

  // onDropCourseUser handles drop events in both GroupConfigCard and PageGroupCard. It consults
  // the value of page_group_id set on the dragged item in moveCourseUserLocally and the value of
  // sourceContainerId from the drop event and performs the appropriate API request. The value of
  // sourceContainerId will be the special value "ungrouped" when the item is being dragged out
  // of the ungrouped students list, or a PageGroup id when being dragged out of a PageGroupCard.
  const onDropCourseUser = (
    droppedItem: DraggableCourseUserType | DraggablePageGroupUserType,
    _toIndex: number,
    sourceContainerId: string,
  ) => {
    if (droppedItem.page_group_id === sourceContainerId) {
      // No-op if the item is being dropped back into the same container it came from.
      return;
    }

    if (sourceContainerId === "ungrouped") {
      // We are grabbing from the ungrouped students list, but they might be just unready in
      // which case they already have a PageGroupUser that needs to be updated rather than created.
      const pageGroupUserId = pageGroups
        .flatMap((pg) => pg.page_group_users)
        .find((pgu) => pgu.course_user_id === droppedItem.course_user_id)?.id;

      if (pageGroupUserId) {
        // Existing PageGroupUser found, so update it to be ready and in the new PageGroup.
        storeApi.page_group_users.partial_update({
          id: pageGroupUserId,
          page_group_id: droppedItem.page_group_id,
          is_ready: true,
        });
      } else {
        // No existing PageGroupUser, so create a new one.
        storeApi.page_group_users.create({
          course_id: page.course_id,
          page_group_id: droppedItem.page_group_id,
          course_user_id: droppedItem.course_user_id,
          is_ready: true,
        });
      }
    } else if (droppedItem.page_group_id === "ungrouped") {
      // A DraggablePageGroupUserType object is being dragged from a PageGroupCard and dropped
      // into the ungrouped students list, and the corresponding PageGroupUser needs to be deleted.
      storeApi.page_group_users.destroy(droppedItem.id);
    } else {
      // A DraggablePageGroupUserType object is being dragged from one PageGroupCard and dropped
      // into another, and the corresponding PageGroupUser needs to be updated.
      storeApi.page_group_users.partial_update({
        id: droppedItem.id,
        page_group_id: droppedItem.page_group_id,
        is_ready: true,
      });
    }
  };

  // If all course_users are grouped but there is a group that exceeds the max group size limit,
  // the Distribute Students button should be re-enabled.
  const isAssignedGroupsInteractionMode = page.interaction_mode === InteractionMode.ASSIGN_STUDENTS;
  const isStudentSelfAssignInteractionMode = page.interaction_mode === InteractionMode.SELF_ASSIGN;
  const areAllStudentsGrouped = ungroupedStudentUsers.length === 0;
  const isMaxGroupSizeSet = page.max_group_size !== null;
  // Consider a group at or above the size limit as a candidate for redistribution.
  const isAnyGroupAtOrOverSizeLimit = pageGroups.some((pg) =>
    hasTotalUsersReachedMaxGroupSize(pg, page),
  );
  const noStudentsLeftToDistribute =
    areAllStudentsGrouped && (!isMaxGroupSizeSet || !isAnyGroupAtOrOverSizeLimit);
  const isDistributeDisabled =
    !isAssignedGroupsInteractionMode ||
    noStudentsLeftToDistribute ||
    isDistributingStudents ||
    isReadOnly;
  const distributeStudentsButtonTooltip = hasAssessments
    ? t("tooltip.group_config_has_assessments")
    : noStudentsLeftToDistribute
      ? t("tooltip.distribute.no_students")
      : t("tooltip.distribute.default");

  const onDistributeStudents = () => {
    setIsDistributingStudents(true);
    const courseUserIds = isInVirtualClass
      ? users
          .filter(
            (u) =>
              u.role === "student" && u.courseActivityStatus !== CourseUserActivityStatus.OFFLINE,
          )
          .map((u) => u.course_user_id)
      : null;

    serverDistributeUsersToGroups(page.id, courseUserIds)
      .catch(() => toastLocalizedOperationCatcher("failed_to_distribute_users_to_groups"))
      .finally(() => setIsDistributingStudents(false));
  };

  const isResetGroupsDisabled =
    pageGroups.reduce((acc, curr) => curr.page_group_users.length + acc, 0) === 0 || isReadOnly;

  const onResetGroups = () => {
    // TODO: This should be a batch delete all or delete many.
    pageGroups.forEach((pageGroup) => {
      pageGroup.page_group_users.forEach((pageGroupUser) => {
        storeApi.page_group_users.destroy(pageGroupUser.id);
      });
    });
  };

  const styleVars: CSSProperties & Record<"--vc-controls-height", string> = {
    "--vc-controls-height": isInVirtualClass ? "56px" : "0px",
  };

  // Max height calculation subtracts UI elements from viewport height:
  // - --navbar-height, --footer-height: main navigation elements
  // - --vc-controls-height: virtual class controls (56px in VC, 0px otherwise)
  // - --participant-bar-height: virtual class participant bar (virtual class only)
  // - 40px: margins/padding around component
  // - 148px/88px: additional UI elements (88px in virtual class accounts for repositioned elements)
  return (
    <div
      className="group-config-card group-card flex w-full flex-col items-center justify-start gap-4 rounded-2xl border border-black-tint-90 bg-black-tint-97 px-5 py-4 [max-height:calc(100vh-var(--navbar-height)-var(--footer-height)-var(--vc-controls-height)-40px-148px)] data-[virtual-class]:[max-height:calc(100vh-var(--navbar-height)-var(--footer-height)-var(--participant-bar-height)-var(--vc-controls-height)-40px-88px)]"
      data-virtual-class={isInVirtualClass || undefined}
      style={styleVars}
    >
      <div className="flex w-full flex-shrink-0 flex-wrap justify-between">
        <div className="h2 truncate">{t("group_configuration_card.groups")}</div>
        <div className="flex flex-wrap gap-1">
          <InteractionModeConfig
            isReadOnly={isReadOnly || hasSubmissions}
            page={page}
            pageGroups={pageGroups}
            title={
              hasSubmissions
                ? t("tooltip.group_config_has_submissions")
                : t("group_configuration_card.tooltip.group_selection_settings")
            }
          />

          <CloseButton
            size="s"
            title={t("group_configuration_card.tooltip.close_group_settings")}
            onClick={hideGroupConfig}
          />
        </div>
      </div>

      <GroupConfigBreakoutsBanner hasAssessments={hasAssessments} page={page} />

      {isAssessing && !page.assessments_published_at && (
        <div className="w-full">
          <Banner kind="info">{t("group_configuration_card.anonymity_alert")}</Banner>
        </div>
      )}

      <div className="flex min-h-0 w-full min-w-0 max-w-full flex-1 gap-3 overflow-hidden">
        <div className="panel flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          <DragAndDrop
            className="flex min-h-0 w-full flex-1"
            dragItemKind="course_user"
            dropContainerId="ungrouped"
            droppable
            onDraggedItemHover={moveCourseUserLocally}
          >
            <Card
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
              data-testid="students-card"
            >
              <div className="mb-1 flex w-full justify-between">
                <div className="h3 ml-2 truncate">{t("group_configuration_card.students")}</div>
                <div className="h6 flex items-center justify-center gap-1 px-2 text-black-tint-40">
                  <PersonSingleIcon />
                  {ungroupedStudentUsers.length}/{studentUsers.length}
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto">
                {ungroupedStudentUsers.map((course_user) => (
                  <DragAndDrop
                    className="w-full"
                    dragItemKind="course_user"
                    dropContainerId="ungrouped"
                    item={course_user}
                    key={course_user.id}
                    draggable
                    onDropItem={onDropCourseUser}
                  >
                    <ListItem
                      className="flex w-full items-center justify-between"
                      data-course-user-id={course_user.course_user_id}
                      data-testid={`row-${course_user.course_user_id}`}
                      key={course_user.id}
                      grabbable
                    >
                      <div className="flex min-w-0 items-center gap-1.5">
                        {userActivityIcons[course_user.courseActivityStatus]}
                        <div className="truncate">{getUserFullName(course_user)}</div>
                      </div>

                      <StudentOptionsMenu
                        currentGroupId={undefined}
                        isInGroup={false}
                        page={page}
                        pageGroups={pageGroups}
                        studentCourseUserId={course_user.course_user_id}
                      />
                    </ListItem>
                  </DragAndDrop>
                ))}
              </div>
            </Card>
          </DragAndDrop>
        </div>

        {isDistributingStudents ? (
          <div className="panel flex w-full justify-center">
            <div className="pt-7">
              <LoadingDotsIcon />
            </div>
          </div>
        ) : (
          <div className="panel flex min-h-0 min-w-0 flex-[0_0_50%] flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="flex flex-col gap-3">
                {pageGroups.map((pageGroup, index) => (
                  <PageGroupCard
                    canDeleteGroup={!isReadOnly}
                    key={pageGroup.id}
                    moveCourseUserLocally={moveCourseUserLocally}
                    number={index + 1}
                    page={page}
                    pageGroup={pageGroup}
                    pageGroups={pageGroups}
                    totalPageGroups={pageGroups.length}
                    onDropCourseUser={onDropCourseUser}
                    onRemove={moveRemovedStudentToTop}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex w-full flex-shrink-0 flex-wrap justify-between">
        <Tooltip
          text={
            hasAssessments ? t("tooltip.group_config_has_assessments") : t("tooltip.reset_groups")
          }
        >
          <LegacyButton
            disabled={isResetGroupsDisabled}
            eventName="Group settings - Reset Groups"
            kind="tertiary"
            size="s"
            onClick={onResetGroups}
          >
            {t("group_configuration_card.reset")}
          </LegacyButton>
        </Tooltip>

        <div className="flex gap-1">
          <TextAddButton
            className="add-page-group"
            eventName="Group settings - Add new group"
            kind="white"
            reference="page_group"
            size="s"
            onClick={() => {
              storeApi.page_groups.create({
                page_id: page.id,
                course_id: page.course_id,
              });
            }}
          />

          {isAssignedGroupsInteractionMode && (
            <>
              <GroupSizeConfig
                isReadOnly={isReadOnly}
                page={page}
                title={
                  hasAssessments
                    ? t("tooltip.group_config_has_assessments")
                    : t("tooltip.group_size")
                }
              />

              <Tooltip text={isDistributeDisabled ? distributeStudentsButtonTooltip : ""}>
                <LegacyButton
                  disabled={isDistributeDisabled}
                  eventName="Group settings - Distribute students"
                  kind="secondary"
                  size="s"
                  title={!isDistributeDisabled ? distributeStudentsButtonTooltip : ""}
                  onClick={onDistributeStudents}
                >
                  {isInVirtualClass
                    ? t("group_configuration_card.distribute_online_students")
                    : t("group_configuration_card.distribute_students")}
                </LegacyButton>
              </Tooltip>
            </>
          )}

          {isStudentSelfAssignInteractionMode && (
            <GroupSizeConfig
              isReadOnly={isReadOnly}
              page={page}
              title={
                hasAssessments ? t("tooltip.group_config_has_assessments") : t("tooltip.group_size")
              }
            />
          )}

          <LegacyButton
            eventName="Group settings - Done"
            eventProperties={{ pageGroupCount: pageGroups.length }}
            kind="primary"
            size="s"
            onClick={hideGroupConfig}
          >
            {t("common.done")}
          </LegacyButton>
        </div>
      </div>
    </div>
  );
};
